import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, TextField, IconButton, ClickAwayListener, Popper } from "@mui/material";
import styles from "header/styles.module.scss";
import { constructSwatchImageUrl } from "utilities/imageUrlUtils";
import colorHexMap from "../../../constants/colorSwatchHexes";

const getHexCode = (color) => {
  const formattedColor = color
    ?.replace(/\s/g, "")
    .replace(/\./g, "")
    .toLowerCase()
    .split("/");
  const hexKey = formattedColor?.join("");
  return colorHexMap[hexKey];
};

const getColorGradient = (hexCodes) => {
  if (!hexCodes) return "transparent";
  if (hexCodes.length === 1) {
    return `linear-gradient(90deg, ${hexCodes[0]} 0%, ${hexCodes[0]} 100%)`;
  }
};

// Custom swatch component that tries to load image first, then falls back to hex color
const ColorSwatch = ({ color, dgiStyleSku, size = 20, extraStyles = {} }) => {
  const [imageLoaded, setImageLoaded] = useState("loading");
  const [swatchStyle, setSwatchStyle] = useState({});

  // Get the swatch image URL
  const swatchImageUrl = dgiStyleSku ? constructSwatchImageUrl(dgiStyleSku, color) : null;

  // Try to load the image
  useEffect(() => {
    if (!swatchImageUrl) {
      setImageLoaded("error");
      return;
    }

    const image = new Image();
    image.src = swatchImageUrl;
    image.onload = () => {
      setImageLoaded("loaded");
    };
    image.onerror = (error) => {
      setImageLoaded("error");
    };
  }, [swatchImageUrl, color]);

  // Set the appropriate style based on image load state
  useEffect(() => {
    if (imageLoaded === "loaded" && swatchImageUrl) {
      setSwatchStyle({
        background: `url("${swatchImageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      });
    } else if (imageLoaded === "error") {
      const colorHexCodes = getHexCode(color);
      const subColorGradient = getColorGradient(colorHexCodes);
      setSwatchStyle({
        background: subColorGradient,
      });
    }
  }, [imageLoaded, color, swatchImageUrl]);

  return (
    <div 
      className="flex-shrink-0 rounded-sm border border-solid border-[rgba(0,0,0,0.1)]"
      style={{ 
        width: `${size}px`, 
        height: `${size}px`,
        borderRadius: '4px',
        ...swatchStyle,
        ...extraStyles
      }}
    />
  );
};

const ColorOption = ({
  color,
  onColorSelect,
  searchValue,
  isSelected,
  index,
  onMouseEnter,
  dgiStyleSku,
}) => {
  const optionRef = useRef(null);
  
  // Scroll into view if selected
  useEffect(() => {
    if (isSelected && optionRef.current) {
      optionRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);
  
  // Create a function to highlight matching text
  const highlightMatch = (text, searchValue) => {
    if (!searchValue) return <span className="font-normal" style={{ fontSize: '0.9rem' }}>{text}</span>;
    
    const parts = text.split(new RegExp(`(${searchValue})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchValue.toLowerCase() ? 
        <span key={index} className="font-bold" style={{ fontSize: '0.9rem' }}>{part}</span> : 
        <span key={index} className="font-normal" style={{ fontSize: '0.9rem' }}>{part}</span>
    );
  };

  return (
    <div
      ref={optionRef}
      className={`
        flex items-center px-[10px] py-[6px]
        w-full
        ${isSelected ? 'bg-lightestGrey' : 'hover:bg-lightestGrey'}
        cursor-pointer text-wolfGrey
      `}
      style={{ fontSize: '0.75rem' }}
      onClick={() => {
        onColorSelect(color);
      }}
      onMouseEnter={() => onMouseEnter(index)}
    >
      <div className="flex items-center gap-2">
        <ColorSwatch 
          color={color} 
          dgiStyleSku={dgiStyleSku}
          size={18}
          extraStyles={{ marginLeft: '4px' }}
        />
        <div className="text-xs" style={{ marginLeft: '12px', fontSize: '0.9rem' }}>
          {highlightMatch(color, searchValue)}
        </div>
      </div>
    </div>
  );
};

const ColorResults = ({
  colors,
  inputValue,
  onColorSelect,
  dgiStyleSku,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const resultsContainerRef = useRef(null);
  
  // Filter colors based on input value
  const filteredColors = colors.filter(color => 
    !inputValue || color.toLowerCase().includes(inputValue.toLowerCase())
  );
  
  // Reset selected index when filtered colors change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [filteredColors.length, inputValue]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!filteredColors.length) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex < filteredColors.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex > 0 ? prevIndex - 1 : filteredColors.length - 1;
            return nextIndex;
          });
          break;
        case 'Tab':
          // Prevent default tab behavior to keep focus in the input
          e.preventDefault();
          setSelectedIndex(prevIndex => {
            const nextIndex = prevIndex < filteredColors.length - 1 ? prevIndex + 1 : 0;
            return nextIndex;
          });
          break;
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < filteredColors.length) {
            e.preventDefault();
            onColorSelect(filteredColors[selectedIndex]);
          }
          break;
        default:
          break;
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filteredColors, selectedIndex, onColorSelect]);

  const handleMouseEnter = (index) => {
    setSelectedIndex(index);
  };

  if (filteredColors.length === 0) {
    return (
      <div className="py-4 px-6 text-center">
        <Typography color="text.secondary" className="text-xs">
          No colors match your search
        </Typography>
      </div>
    );
  }

  return (
    <div 
      ref={resultsContainerRef} 
      className="w-full h-full overflow-y-auto rounded-bl-[25px] rounded-br-[25px] text-xs"
      style={{ fontSize: '0.75rem' }}
    >
      {filteredColors.map((color, index) => (
        <ColorOption
          key={color}
          color={color}
          onColorSelect={onColorSelect}
          searchValue={inputValue}
          isSelected={index === selectedIndex}
          index={index}
          onMouseEnter={handleMouseEnter}
          dgiStyleSku={dgiStyleSku}
        />
      ))}
    </div>
  );
};

const FastEntryColorSearch = forwardRef(({
  item,
  onColorSelect,
  availableColors = [],
  selectedStyle,
  initialSelectedColor,
  placeholder = "Select color...",
  showLabel = false,
  disabled = false,
}, ref) => {
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  
  // Add a ref to track color selection
  const colorSelectionRef = useRef(false);

  // Combine the two useEffects into one more comprehensive effect
  useEffect(() => {
    // When availableColors changes and we have a selectedColor or initialSelectedColor
    if (availableColors.length > 0) {
      // Priority 1: Use the current selectedColor if it exists in availableColors
      if (selectedColor && availableColors.includes(selectedColor)) {
        setInputValue(selectedColor);
        setIsFocused(false);
      }
      // Priority 2: Use initialSelectedColor if it exists in availableColors
      else if (initialSelectedColor && availableColors.includes(initialSelectedColor) && !colorSelectionRef.current) {
        setInputValue(initialSelectedColor);
        setSelectedColor(initialSelectedColor);
        onColorSelect(initialSelectedColor);
        colorSelectionRef.current = true;
        setIsFocused(false);
      }
      // Priority 3: Use item.color if it exists in availableColors
      else if (item?.color && availableColors.includes(item.color)) {
        setInputValue(item.color);
        setSelectedColor(item.color);
        onColorSelect(item.color);
        setIsFocused(false);
      }
    }
  }, [availableColors, selectedColor, initialSelectedColor, item]);

  // Expose the focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current && !disabled) {
        // Only focus if not disabled
        setIsFocused(true);
        setTimeout(() => {
          inputRef.current.focus();
        }, 0);
      }
    },
    closePopper: () => {
      setIsFocused(false);
    }
  }));

  const handleKeyDown = (event) => {
    if (disabled) return; // Don't process keyboard events if disabled
    
    if (event.key === "Enter" && !event.isDefaultPrevented()) {
      // Default Enter behavior if no item is selected
      const filteredColors = availableColors.filter(color => 
        !inputValue || color.toLowerCase().includes(inputValue.toLowerCase())
      );
      if (filteredColors.length > 0) {
        event.preventDefault();
        onColorSelect(filteredColors[0]);
        setSelectedColor(filteredColors[0]);
      }
    }
  };

  // Get the dgi_style_sku from the selectedStyle
  const dgiStyleSku = selectedStyle?.dgi_style_sku || null;

  return (
    <div>
      {showLabel && (
        <Typography 
          variant="h6" 
          className="text-xl font-medium mb-4"
          style={{ 
            marginBottom: '1rem',
            fontSize: '1.25rem',
            fontWeight: 400
          }}
        >
          Select New Color
        </Typography>
      )}
      
      <ClickAwayListener onClickAway={() => {
        setIsFocused(false);
      }}>
        <div style={{ position: 'relative' }}>
          {/* Color Search Input Container - Fixed Height */}
          <div
            ref={containerRef}
            className={`
              w-full bg-white 
              h-9 
              rounded-tr-[20px] rounded-tl-[20px]
              ${!isFocused ? "rounded-br-[20px] rounded-bl-[20px]" : ""}
              border border-solid border-[rgba(0,0,0,0.12)]
              ${isFocused ? "border-b-0" : ""}`}
            style={{ 
              position: 'relative', 
              zIndex: 1000,
              opacity: disabled ? 0.7 : 1,
              backgroundColor: disabled ? '#e0e0e0' : 'white'
            }}
          >
            <div className="flex flex-row items-end gap-[0.5rem] w-full h-full p-[2px]">
              <div className="flex-grow h-full w-full relative">
                <TextField
                  fullWidth
                  inputRef={inputRef}
                  autoComplete="off"
                  value={inputValue}
                  onChange={(e) => {
                    if (!disabled) setInputValue(e.target.value);
                  }}
                  onFocus={(e) => {
                    if (!disabled) {
                      setIsFocused(true);
                      e.target.select();
                    }
                  }}
                  placeholder={placeholder}
                  variant="standard"
                  className="pb-[1px]"
                  disabled={disabled}
                  InputProps={{
                    disableUnderline: true,
                    className: `custom-search-input ${disabled ? 'bg-[#e0e0e0]' : 'bg-white'} w-full min-h-full rounded-l-full rounded-r-full flex justify-start gap-[0.5rem] pt-[0px]`,
                    classes: {
                      underline: "custom-underline",
                    },
                    startAdornment: (
                      <div style={{ marginLeft: '12px' }}>
                        {selectedColor ? (
                          <ColorSwatch 
                            color={selectedColor} 
                            dgiStyleSku={dgiStyleSku}
                            size={18}
                          />
                        ) : (
                          <div 
                            className="flex-shrink-0 rounded-sm border border-solid border-[rgba(0,0,0,0.1)]"
                            style={{ 
                              width: '18px', 
                              height: '18px',
                              borderRadius: '4px',
                              background: 'transparent'
                            }}
                          />
                        )}
                      </div>
                    ),
                    inputProps: {
                      className: `${styles.customPlaceholder} ${
                        inputValue.length > 0 && "pt-[6px]"
                      }`,
                      style: { 
                        paddingLeft: '4px',
                        fontSize: '0.9rem',
                        color: disabled ? '#757575' : 'inherit'
                      }
                    },
                  }}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          </div>

          {/* Color Results Dropdown - Using Popper to ensure proper overlay */}
          <Popper
            open={isFocused && !disabled}
            anchorEl={containerRef.current}
            placement="bottom-start"
            style={{ width: containerRef.current?.offsetWidth || 'auto', zIndex: 1001 }}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: false,
              },
              {
                name: 'flip',
                enabled: false,
              }
            ]}
          >
            <div 
              className="border border-solid border-[rgba(0,0,0,0.12)] bg-white"
              style={{
                maxHeight: '300px',
                borderBottomLeftRadius: '20px',
                borderBottomRightRadius: '20px',
                boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                borderTop: '1px dashed rgba(0,0,0,0.12)'
              }}
            >
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <ColorResults
                  colors={availableColors}
                  inputValue={inputValue}
                  onColorSelect={(color) => {
                    onColorSelect(color);
                    setSelectedColor(color);
                    setInputValue(color);
                    setIsFocused(false);
                  }}
                  dgiStyleSku={dgiStyleSku}
                />
              </div>
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
});

export default FastEntryColorSearch;